import type Parse from 'parse';

import { Resource } from '@telespot/domain';

import { ParseMapper } from '../../parse.mapper';
import { ObjectTopology, SoftDeletableTopology } from '../../parse.topology';

export enum ResourceTopology {
  TABLE = 'Resource',
  NAME = 'name',
  TYPE = 'type',
  RESOURCES = 'resources',
  VERSION = 'version',
  DESCRIPTION = 'description',
  VALUE = 'value',
  METADATA = 'metadata',
  DISPLAY_NAME = 'displayName',
}

const MAX_DEPTH = 2;

export class ParseResourceMapper extends ParseMapper {
  public toDomain(parseObject: Parse.Object, depth = 0): Resource {
    const nestedResources =
      depth < MAX_DEPTH
        ? parseObject.get(ResourceTopology.RESOURCES)?.map((r) => this.toDomain(r, depth + 1)) || []
        : [];
    return new Resource({
      id: parseObject.id,
      name: parseObject.get(ResourceTopology.NAME),
      type: parseObject.get(ResourceTopology.TYPE),
      resources: nestedResources || [],
      version: parseObject.get(ResourceTopology.VERSION),
      description: parseObject.get(ResourceTopology.DESCRIPTION),
      value: parseObject.get(ResourceTopology.VALUE),
      metadata: parseObject.get(ResourceTopology.METADATA),
      displayName: parseObject.get(ResourceTopology.DISPLAY_NAME),
      updatedAt: parseObject.get(ObjectTopology.UPDATED_AT),
      createdAt: parseObject.get(ObjectTopology.CREATED_AT),
      deletedAt: parseObject.get(SoftDeletableTopology.DELETED_AT),
    });
  }

  public fromDomain(resource: Resource): Parse.Object {
    const ParseResource = this.subclasses.getSubclass(ResourceTopology.TABLE);

    const parseResourceReferences = resource.resources.map((r) =>
      ParseResource.createWithoutData(r.id)
    );

    const parseObject = resource.id
      ? this.parse.Object.fromJSON({
          className: ResourceTopology.TABLE,
          [ObjectTopology.ID]: resource.id,
          [ObjectTopology.CREATED_AT]: resource.createdAt,
          [ObjectTopology.UPDATED_AT]: resource.updatedAt,
        })
      : new ParseResource();

    parseObject.set({
      [SoftDeletableTopology.DELETED_AT]: resource.deletedAt,
      [ResourceTopology.NAME]: resource.name,
      [ResourceTopology.TYPE]: resource.type,
      [ResourceTopology.VERSION]: resource.version,
      [ResourceTopology.VALUE]: resource.value,
      [ResourceTopology.DESCRIPTION]: resource.description,
      [ResourceTopology.RESOURCES]: parseResourceReferences,
      [ResourceTopology.METADATA]: resource.metadata,
      [ResourceTopology.DISPLAY_NAME]: resource.displayName,
    });

    return parseObject;
  }
}
